import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { captureUtmsOnce } from "@/lib/utm";
import Landing from "@/pages/Landing";
import About from "@/pages/About";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Cases from "@/pages/Cases";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import AppShell from "@/pages/AppShell";
import Dashboard from "@/pages/Dashboard";
import Forecast from "@/pages/Forecast";
import Alerts from "@/pages/Alerts";
import Copilot from "@/pages/Copilot";
import Recommendations from "@/pages/Recommendations";
import Catalog from "@/pages/Catalog";
import Seasonality from "@/pages/Seasonality";
import Financial from "@/pages/Financial";
import Simulator from "@/pages/Simulator";
import Stores from "@/pages/Stores";
import Benchmarking from "@/pages/Benchmarking";
import Integrations from "@/pages/Integrations";
import Profile from "@/pages/Profile";
import AdminShell from "@/pages/admin/AdminShell";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import AdminDatabase from "@/pages/admin/AdminDatabase";
import AdminLogs from "@/pages/admin/AdminLogs";
import Pricing from "@/pages/Pricing";
import BillingSuccess from "@/pages/BillingSuccess";
import PlanGate from "@/components/PlanGate";
import ScrollToHash from "@/components/ScrollToHash";
import { Toaster } from "@/components/ui/sonner";

const Protected = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return <Navigate to="/login" replace />;
  const state = user.plan_state || {};
  // Force pricing for any user without an effective plan (expired/canceled/pending_payment)
  const noAccess =
    !user.is_admin &&
    !state.effective_plan &&
    state.plan_status !== "trial";
  const path = location.pathname;
  const allowed = path === "/app/pricing" || path === "/app/profile";
  if (noAccess && !allowed) return <Navigate to="/app/pricing" replace />;
  return children;
};

function App() {
  useEffect(() => {
    captureUtmsOnce();
  }, []);
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToHash />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/sobre" element={<About />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/cases" element={<Cases />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/billing/success" element={<BillingSuccess />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route
              path="/app"
              element={
                <Protected>
                  <AppShell />
                </Protected>
              }
            >
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="forecast" element={<PlanGate feature="forecast"><Forecast /></PlanGate>} />
              <Route path="alerts" element={<Alerts />} />
              <Route path="copilot" element={<PlanGate feature="copilot"><Copilot /></PlanGate>} />
              <Route path="recommendations" element={<PlanGate feature="recommendations"><Recommendations /></PlanGate>} />
              <Route path="catalog" element={<Catalog />} />
              <Route path="seasonality" element={<PlanGate feature="seasonality"><Seasonality /></PlanGate>} />
              <Route path="financial" element={<PlanGate feature="financial"><Financial /></PlanGate>} />
              <Route path="simulator" element={<PlanGate feature="simulator"><Simulator /></PlanGate>} />
              <Route path="stores" element={<PlanGate feature="stores"><Stores /></PlanGate>} />
              <Route path="benchmarking" element={<PlanGate feature="benchmarking"><Benchmarking /></PlanGate>} />
              <Route path="integrations" element={<Integrations />} />
              <Route path="profile" element={<Profile />} />
              <Route path="pricing" element={<Pricing />} />
            </Route>
            <Route path="/admin" element={<AdminShell />}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="subscriptions" element={<AdminSubscriptions />} />
              <Route path="database" element={<AdminDatabase />} />
              <Route path="logs" element={<AdminLogs />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster theme="dark" position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
